import React from 'react';
import { apiJson } from '../api';
import TrophySvg from './TrophySvg';

/* ====================================================================
   LEADERBOARD PANEL — sortable results table with win rate
   ==================================================================== */
function LeaderboardPanel({ leaderboard, selectedComp, isAdmin, completedMatches, totalPlayable, refreshComp, handleCompTeamClick, setActiveStep, showToast, setSelectedComp, setComps }) {
  const [sortKey, setSortKey] = React.useState(null); // null = default server order
  const [sortDir, setSortDir] = React.useState('desc');

  /* Finalising and reopening are the same request with a different word, and
     both used to be written as `.then(r => r.json()).then(setSelectedComp)`.
     fetch does not reject on a 403, so a refused finalise replaced the whole
     competition — name, format, dates, everything — with { error: ... }, and
     then announced "Competition finalised!" over the top of it. Throwing on a
     refusal means the competition on screen is only ever replaced by a real
     competition. */
  const setCompetitionStatus = async (status) => {
    const updated = await apiJson(`/api/competitions/${selectedComp.id}`, {
      method: 'PUT', body: { status },
    });
    setSelectedComp(updated);
    setComps(prev => prev.map(x => x.id === updated.id ? updated : x));
    refreshComp();
    return updated;
  };
  const noDraws = selectedComp?.matchType === 'first-to' || selectedComp?.tournamentType === 'double-elimination' || !!selectedComp?.extraEndOnDraw;

  const toggleSort = (key) => {
    if (sortKey === key) {
      if (sortDir === 'desc') setSortDir('asc');
      else { setSortKey(null); setSortDir('desc'); } // third click resets to default
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return <i className="fa fa-sort" style={{ opacity: 0.3, marginLeft: 4 }}></i>;
    return <i className={`fa fa-sort-${sortDir === 'desc' ? 'down' : 'up'}`} style={{ marginLeft: 4 }}></i>;
  };

  const thStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

  // Compute sorted standings
  const standings = React.useMemo(() => {
    if (!leaderboard || !leaderboard.standings) return [];
    const rows = leaderboard.standings.map((s, i) => ({
      ...s,
      _origIdx: i,
      winRate: (s.played - (s.byes || 0)) > 0 ? (s.wins / (s.played - (s.byes || 0))) * 100 : 0,
    }));
    if (!sortKey) return rows;
    const sorted = [...rows].sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (sortKey === 'teamName') {
        av = (av || '').toLowerCase(); bv = (bv || '').toLowerCase();
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === 'desc' ? (bv - av) : (av - bv);
    });
    return sorted;
  }, [leaderboard, sortKey, sortDir]);

  return (
    <div className="w3-card w3-round w3-white w3-margin-top">
      <div className="w3-container w3-padding">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
          <h4><TrophySvg size={18} style={{ marginRight: '8px' }} />Results & Standings</h4>
          <div>
            <button className="w3-button w3-small w3-theme-l4 w3-round w3-margin-right" onClick={() => setActiveStep('analytics')}>
              <i className="fa fa-bar-chart w3-margin-right"></i>Analytics
            </button>
            <button className="w3-button w3-small w3-theme-l4 w3-round" onClick={refreshComp}>
              <i className="fa fa-refresh w3-margin-right"></i>Refresh
            </button>
          </div>
        </div>

        {standings.length > 0 ? (
          <div className="w3-responsive">
            <table className="w3-table w3-bordered w3-small">
              <thead className="w3-theme-d5">
                <tr>
                  <th>#</th>
                  <th style={thStyle} onClick={() => toggleSort('teamName')}>Team{sortIcon('teamName')}</th>
                  <th style={thStyle} onClick={() => toggleSort('played')}>Played{sortIcon('played')}</th>
                  <th style={thStyle} onClick={() => toggleSort('wins')}>Won{sortIcon('wins')}</th>
                  {!noDraws && <th style={thStyle} onClick={() => toggleSort('draws')}>Drawn{sortIcon('draws')}</th>}
                  <th style={thStyle} onClick={() => toggleSort('losses')}>Lost{sortIcon('losses')}</th>
                  <th style={thStyle} onClick={() => toggleSort('byes')}>Byes{sortIcon('byes')}</th>
                  <th style={thStyle} onClick={() => toggleSort('winRate')}>Win%{sortIcon('winRate')}</th>
                  <th style={thStyle} onClick={() => toggleSort('gamePoints')}>Points{sortIcon('gamePoints')}</th>
                  <th style={thStyle} onClick={() => toggleSort('pointsFor')}>Points For{sortIcon('pointsFor')}</th>
                  <th style={thStyle} onClick={() => toggleSort('pointsAgainst')}>Against{sortIcon('pointsAgainst')}</th>
                  <th style={thStyle} onClick={() => toggleSort('pointsDiff')}>Difference{sortIcon('pointsDiff')}</th>
                  <th style={thStyle} onClick={() => toggleSort('endsWon')}>Ends Won{sortIcon('endsWon')}</th>
                </tr>
              </thead>
              <tbody>
                {standings.map((s, i) => {
                  const origIdx = sortKey ? i : s._origIdx;
                  return (
                    <tr key={s.teamId} className="w3-hover-light-grey" style={{ cursor: 'pointer', ...(!sortKey && origIdx === 0 ? { backgroundColor: '#fff9c4' } : !sortKey && origIdx === 1 ? { backgroundColor: '#e0e0e0' } : !sortKey && origIdx === 2 ? { backgroundColor: '#ffe0b2' } : {}) }}
                      onClick={(e) => handleCompTeamClick(s.teamId, e)}>
                      <td><strong>{sortKey ? i + 1 : s.position}</strong></td>
                      <td>
                        <strong>{s.teamName}</strong>
                        {s.players && s.players.length > 0 && !(s.players.length === 1 && s.players[0] === s.teamName) && (<br />)}
                        {s.players && s.players.length > 0 && !(s.players.length === 1 && s.players[0] === s.teamName) && (
                          <span className="w3-tiny w3-text-grey">{s.players.join(', ')}</span>
                        )}
                      </td>
                      <td>{s.played}</td>
                      <td>{s.wins}</td>
                      {!noDraws && <td>{s.draws}</td>}
                      <td>{s.losses}</td>
                      <td>{s.byes}</td>
                      <td>{s.winRate.toFixed(0)}%</td>
                      <td><strong>{s.gamePoints}</strong></td>
                      <td>{s.pointsFor}</td>
                      <td>{s.pointsAgainst}</td>
                      <td className={s.pointsDiff > 0 ? 'w3-text-green' : s.pointsDiff < 0 ? 'w3-text-red' : ''}>
                        {s.pointsDiff > 0 ? '+' : ''}{s.pointsDiff}
                      </td>
                      <td>{s.endsWon}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {sortKey && (
              <p className="w3-tiny w3-text-blue" style={{ marginTop: '4px', cursor: 'pointer' }} onClick={() => { setSortKey(null); setSortDir('desc'); }}>
                <i className="fa fa-undo w3-margin-right"></i>Reset to default ranking order
              </p>
            )}
            <p className="w3-tiny w3-text-grey w3-margin-top" style={{ marginBottom: '2px' }}>
              Win = {selectedComp.winPoints} pts, Draw = {selectedComp.drawPoints} pt{selectedComp.drawPoints !== 1 ? 's' : ''}, Loss = {selectedComp.lossPoints} pts, Bye = {selectedComp.byePoints} pts
            </p>
            {selectedComp.scoringMethod === 'twilight' && (
              <p className="w3-tiny w3-text-grey" style={{ marginTop: '0', marginBottom: '2px' }}>
                Twilight scoring bonus: +{selectedComp.pointsPerSetWon || 0} per set won, +{selectedComp.pointsPerEndWon || 0} per end won
              </p>
            )}
            {(() => {
              const labels = { game_points: 'Points', wins: 'Wins', points_diff: 'Points Difference', ends_won: 'Ends Won', points_for: 'Points For', points_against: 'Points Against', head_to_head: 'Head to Head', sets_won: 'Sets Won' };
              let raw = (leaderboard && leaderboard.sortedByText) || null;
              if (raw) return <p className="w3-tiny w3-text-grey" style={{ marginTop: '0' }}>Sorted by: {raw}</p>;
              let arr = selectedComp.tiebreakerOrder;
              if (!arr) arr = ['points_diff', 'ends_won', 'points_for', 'head_to_head'];
              for (let i = 0; i < 5 && typeof arr === 'string'; i++) { try { arr = JSON.parse(arr); } catch(e) { arr = null; break; } }
              if (!Array.isArray(arr)) arr = ['points_diff', 'ends_won', 'points_for', 'head_to_head'];
              const text = ['game_points', ...arr].map(k => labels[k] || k).join(', then ');
              return <p className="w3-tiny w3-text-grey" style={{ marginTop: '0' }}>Sorted by: {text}</p>;
            })()}
          </div>
        ) : (
          <p className="w3-text-grey w3-center"><em>No results yet. Score some matches first.</em></p>
        )}

        {/* Nav & Finalize */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', paddingBottom: '4px' }}>
          <button className="w3-button w3-theme-l4 w3-round" onClick={() => setActiveStep('scores')}>
            <i className="fa fa-arrow-left w3-margin-right"></i>Back to Scores
          </button>
          {isAdmin && selectedComp.status !== 'completed' ? (
            <button className="w3-button w3-green w3-round-large"
              disabled={completedMatches < totalPlayable || totalPlayable === 0}
              onClick={async () => {
                if (completedMatches < totalPlayable) {
                  showToast(`${totalPlayable - completedMatches} match(es) still need scores`, 'amber');
                  return;
                }
                try {
                  await setCompetitionStatus('completed');
                  showToast('Competition finalised!', 'green');
                } catch (err) {
                  showToast(err.message || 'Could not finalise the competition', 'red');
                }
              }}>
              <i className="fa fa-check-circle w3-margin-right"></i>Finalise Competition
              {completedMatches < totalPlayable && <span className="w3-tiny w3-margin-left">({completedMatches}/{totalPlayable} scored)</span>}
            </button>
          ) : isAdmin && selectedComp.status === 'completed' ? (
            <button className="w3-button w3-amber w3-round-large"
              onClick={async () => {
                try {
                  await setCompetitionStatus('reopened');
                  showToast('Competition reopened — scores only', 'amber');
                } catch (err) {
                  showToast(err.message || 'Could not reopen the competition', 'red');
                }
              }}>
              <i className="fa fa-unlock w3-margin-right"></i>Reopen for Score Fixes
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default LeaderboardPanel;
